import { isPlatformBrowser } from '@angular/common';
import {
  computed,
  DOCUMENT,
  inject,
  Injectable,
  Injector,
  PLATFORM_ID,
  Signal,
} from '@angular/core';
import { BackendService } from '@core/services/backend.service';
import { DataService } from '@core/services/data.service';
import { CetoineTagging } from '@core/services/specific/cetoine-tagging.service';
import { environment } from '@environments/environment';
import { ENVIRONMENTS, RETAILERS } from '@shared/constants';
import { TaggingDAO } from '@shared/interfaces';
import { EmitterKeys, ScriptPageKeys, Tagging } from '@shared/models';

type RetailerTaggerFactories = Partial<Record<RETAILERS, () => TaggingDAO>>;

const NOOP_TAGGER: TaggingDAO = {
  init: (): void => undefined,
  emitTag: (): void => undefined,
  loadScript: (): Promise<void> => Promise.resolve(),
};

@Injectable({ providedIn: 'root' })
export class TaggingService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly dataService = inject(DataService);
  private readonly backendService = inject(BackendService);
  private readonly injector = inject(Injector);

  // Factories instead of instances: retailers that don't tag never trigger
  // construction of their tagging class. CetoineTagging is only resolved
  // on first read for a cetoine bundle.
  private readonly retailerTaggerFactories: RetailerTaggerFactories = {
    [RETAILERS.CETOINE]: (): TaggingDAO => this.injector.get(CetoineTagging),
  };

  private readonly activeRetailerTagger: Signal<TaggingDAO> = computed(
    (): TaggingDAO => {
      if (!isPlatformBrowser(this.platformId)) {
        return NOOP_TAGGER;
      }
      const factory =
        this.retailerTaggerFactories[
          this.dataService.environment().retailerName
        ];
      return factory ? factory() : NOOP_TAGGER;
    }
  );

  init(): void {
    this.activeRetailerTagger().init();
  }

  async loadScriptForPage(pageKey: ScriptPageKeys): Promise<void> {
    if (!this.isLiveBuild()) {
      return;
    }
    const tagging = this.taggingConfig();
    if (!tagging) {
      return;
    }
    const entry = tagging.scriptLoader[pageKey];
    try {
      await this.activeRetailerTagger().loadScript(
        entry.pageName,
        entry.pageTemplate,
        entry.withId ? this.backendService.untienotsId : ''
      );
    } catch (error) {
      // Tagging is best-effort: ad blockers and network failures must not
      // bubble up as unhandled rejections (would otherwise hit Sentry).
      console.warn('Tagging script load failed:', (error as Error).message);
    }
  }

  emitTagForKey(emitterKey: EmitterKeys, overrideLevel3?: string): void {
    if (!this.isLiveBuild()) {
      return;
    }
    const tagging = this.taggingConfig();
    if (!tagging) {
      return;
    }
    const entry = tagging.tagEmitter[emitterKey];
    this.activeRetailerTagger().emitTag(
      entry.level2,
      overrideLevel3 ?? entry.level3,
      entry.eventName
    );
  }

  private taggingConfig(): Tagging | undefined {
    const config = this.dataService.config();
    return 'tagging' in config ? config.tagging : undefined;
  }

  private isLiveBuild(): boolean {
    return (
      [ENVIRONMENTS.PRODUCTION, ENVIRONMENTS.STAGING].includes(
        environment.env
      ) || this.isTaggingCypressTest()
    );
  }

  private isTaggingCypressTest(): boolean {
    if (environment.env !== ENVIRONMENTS.CYPRESS) {
      return false;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    return (
      this.document.defaultView?.localStorage.getItem(
        'enableTaggingScripts'
      ) === 'true'
    );
  }
}
